import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './ArrowCursor.css';

// Inšpirované React Bits <TextCursor />, ale namiesto smeru pohybu
// každá šípka rotuje tak, aby ukazovala na cieľové tlačidlo (targetRef).
const ArrowCursor = ({
  zoneRef,
  targetRef,
  onNear,
  nearRadius = 110,
  glyph = '➔', // ➔ heavy wide-headed arrow
  spacing = 55,
  exitDuration = 0.45,
  removalInterval = 40,
  maxPoints = 7
}) => {
  const [trail, setTrail] = useState([]);
  const lastMoveTimeRef = useRef(0);
  const idCounter = useRef(0);
  const nearRef = useRef(false);

  useEffect(() => {
    const zone = zoneRef?.current;
    if (!zone) return;

    // Rešpektuj používateľov, čo preferujú menej pohybu
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Uhol od bodu (v súradniciach zóny) k stredu cieľového tlačidla
    const angleToTarget = (x, y) => {
      const t = targetRef?.current;
      const zr = zone.getBoundingClientRect();
      if (!t) return 0;
      const tr = t.getBoundingClientRect();
      const tx = tr.left + tr.width / 2 - zr.left;
      const ty = tr.top + tr.height / 2 - zr.top;
      return (Math.atan2(ty - y, tx - x) * 180) / Math.PI;
    };

    // Zmena stavu „kurzor je blízko tlačidla" – hlási sa len pri zmene
    const reportNear = (mouseX, mouseY) => {
      const t = targetRef?.current;
      if (!t || !onNear) return;
      const zr = zone.getBoundingClientRect();
      const tr = t.getBoundingClientRect();
      const tx = tr.left + tr.width / 2 - zr.left;
      const ty = tr.top + tr.height / 2 - zr.top;
      const near = Math.hypot(tx - mouseX, ty - mouseY) < nearRadius;
      if (near !== nearRef.current) {
        nearRef.current = near;
        onNear(near);
      }
    };

    const handleMouseMove = e => {
      const rect = zone.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      reportNear(mouseX, mouseY);

      setTrail(prev => {
        const newTrail = [...prev];

        if (newTrail.length === 0) {
          newTrail.push({
            id: idCounter.current++,
            x: mouseX,
            y: mouseY,
            angle: angleToTarget(mouseX, mouseY),
            rnd: Math.random() * 8 - 4
          });
        } else {
          const last = newTrail[newTrail.length - 1];
          const dx = mouseX - last.x;
          const dy = mouseY - last.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance >= spacing) {
            const steps = Math.floor(distance / spacing);
            for (let i = 1; i <= steps; i++) {
              const f = (spacing * i) / distance;
              const nx = last.x + dx * f;
              const ny = last.y + dy * f;
              newTrail.push({
                id: idCounter.current++,
                x: nx,
                y: ny,
                angle: angleToTarget(nx, ny),
                rnd: Math.random() * 8 - 4
              });
            }
          }
        }

        return newTrail.length > maxPoints ? newTrail.slice(newTrail.length - maxPoints) : newTrail;
      });

      lastMoveTimeRef.current = Date.now();
    };

    const handleMouseLeave = () => {
      if (nearRef.current) {
        nearRef.current = false;
        onNear?.(false);
      }
    };

    zone.addEventListener('mousemove', handleMouseMove);
    zone.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      zone.removeEventListener('mousemove', handleMouseMove);
      zone.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [zoneRef, targetRef, onNear, nearRadius, spacing, maxPoints]);

  // Postupné miznutie chvosta keď sa kurzor zastaví
  useEffect(() => {
    const interval = setInterval(() => {
      if (Date.now() - lastMoveTimeRef.current > 120) {
        setTrail(prev => (prev.length > 0 ? prev.slice(1) : prev));
      }
    }, removalInterval);
    return () => clearInterval(interval);
  }, [removalInterval]);

  return (
    <div className="landing-arrow-cursor" aria-hidden="true">
      <AnimatePresence>
        {trail.map((item, idx) => (
          <motion.div
            key={item.id}
            className="landing-arrow-cursor__item"
            style={{ left: item.x, top: item.y }}
            transformTemplate={(_, generated) => `translate(-50%, -50%) ${generated}`}
            initial={{ opacity: 0, scale: 0.6, rotate: item.angle }}
            animate={{
              opacity: 0.25 + (idx / Math.max(trail.length - 1, 1)) * 0.75,
              scale: 1,
              rotate: [item.angle, item.angle + item.rnd, item.angle]
            }}
            exit={{ opacity: 0, scale: 0.4 }}
            transition={{
              opacity: { duration: exitDuration, ease: 'easeOut' },
              scale: { duration: 0.25, ease: 'easeOut' },
              rotate: { duration: 2, ease: 'easeInOut', repeat: Infinity, repeatType: 'mirror' }
            }}
          >
            {glyph}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default ArrowCursor;
